export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

// Keep the storage key, the default and the resolution logic in sync with the
// inline boot script in index.html.
const themeStorageKey = "household.theme";

/**
 * What someone who has never picked a theme gets. Light rather than "system":
 * the app is designed light-first and the public pages are already a light
 * surface, so following the device meant a visitor on a dark-mode phone met a
 * theme nobody had chosen. "system" stays a real choice in Settings — this only
 * moves the answer for a preference that was never expressed. The column
 * default in the database (migration 127) is the same value for the same
 * reason, so a new account and a signed-out visitor agree.
 */
export const defaultThemePreference: ThemePreference = "light";

// A hint, not a session. The inline boot script has to choose a theme before
// any JavaScript of the app has run, and "/" is the landing page for a visitor
// with no session but the Dashboard for everyone else — so it needs to know
// which one it is about to paint. Written when a session appears and cleared
// when it goes; a stale value only costs one wrong frame (#843).
const sessionHintKey = "household.session";

// What the browser paints around the page — its address bar on a phone. The two
// app values are its navy header. The public pages have a white header instead,
// so they need a third: the app's navy above a white landing page read as a
// strip of unrelated chrome.
const browserThemeColors: Record<ResolvedTheme, string> = {
  light: "#133c55",
  dark: "#0f171d"
};

const publicPageThemeColor = "#ffffff";

let systemThemeQuery: MediaQueryList | null = null;
let systemThemeListener: ((event: MediaQueryListEvent) => void) | null = null;

export function normalizeThemePreference(value: unknown): ThemePreference {
  // "system" is listed rather than left to the fallback because it is a choice
  // someone can make, not the absence of one — only an unset or unrecognised
  // value falls through to the default.
  return value === "dark" || value === "light" || value === "system" ? value : defaultThemePreference;
}

export function readStoredThemePreference(): ThemePreference {
  try {
    return normalizeThemePreference(window.localStorage.getItem(themeStorageKey));
  } catch {
    return defaultThemePreference;
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function setDocumentTheme(theme: ResolvedTheme, browserColor = browserThemeColors[theme]): void {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", browserColor);
}

function stopSystemThemeSync(): void {
  if (systemThemeQuery && systemThemeListener) systemThemeQuery.removeEventListener("change", systemThemeListener);
  systemThemeQuery = null;
  systemThemeListener = null;
}

function startSystemThemeSync(): void {
  if (systemThemeListener) return;
  systemThemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
  systemThemeListener = (event) => setDocumentTheme(event.matches ? "dark" : "light");
  systemThemeQuery.addEventListener("change", systemThemeListener);
}

export function rememberSessionHint(hasSession: boolean): void {
  try {
    if (hasSession) window.localStorage.setItem(sessionHintKey, "1");
    else window.localStorage.removeItem(sessionHintKey);
  } catch {
    // Local storage may be unavailable; the boot script then assumes no session,
    // which is the right guess for the visitor the landing page is written for.
  }
}

/**
 * The public pages are a light surface. They render light whatever the visitor's
 * own preference is, and — unlike applyThemePreference — this writes nothing to
 * storage, so leaving them puts the visitor's own theme straight back (#843).
 */
export function applyPublicPageTheme(): void {
  stopSystemThemeSync();
  setDocumentTheme("light", publicPageThemeColor);
}

export function applyThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(themeStorageKey, preference);
  } catch {
    // Local storage may be unavailable in installed or privacy-restricted PWA contexts.
  }
  if (preference === "system") {
    startSystemThemeSync();
    setDocumentTheme(systemTheme());
    return;
  }
  stopSystemThemeSync();
  setDocumentTheme(preference);
}
